import { gql, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  GachaTicketConnectionFragmentDoc,
  WalletQuery,
  WalletQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getAccessToken, useAuth, useCurrentUser } from '@/core/auth'
import { useDeviceInfo } from '@/core/native/device'
import { getBalance, useMetamask } from '@/core/web3/metamask'
import { useWeb3authWalletConnect } from '@/core/web3/web3auth/hooks'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { linkEmail } = useAuth()
  const { deviceInfo } = useDeviceInfo()
  const { t } = useTranslation('wallet')
  const [balance, setBalance] = useState('')
  const { data, loading, refetch } = useQuery<
    WalletQuery,
    WalletQueryVariables
  >(QUERY, {
    variables: { gachaTicketId: `organization:${config.organizationId}` },
  })

  const walletAddress = data?.user?.cryptoWallet?.address

  const { userAuthorizeWalletBySignatureMessage } = useMetamask()
  const { web3authWalletLoading } = useWeb3authWalletConnect({
    loading: loading,
    walletAddress,
    isArtist: false,
    refetch,
  })

  useEffect(() => {
    if (!config.modules.nft || !walletAddress) return

    const t = async () => {
      const balance = await getBalance(walletAddress)
      setBalance(balance)
    }
    t()
  }, [walletAddress])

  return {
    state: {
      loading: loading || web3authWalletLoading,
      user,
      pointBalance:
        data?.prepaidPointBalance.formattedPrepaidPointBarance ?? null,
      ticket: data?.user?.gachaTicket?.formattedAvailable ?? null,
      gachaTicket: data?.gachaTicket ?? null,
      cryptoWallet: {
        displayName: data?.user?.displayName ?? null,
        walletAddress: walletAddress ?? null,
        balance,
        walletType: data?.user?.cryptoWallet?.walletType ?? null,
      },
    },
    actions: {
      connectMetamask: async () => {
        await userAuthorizeWalletBySignatureMessage()
        await refetch()

        return
      },
      connectToWeb3auth: async () => {
        /**
         * web3auth <=> firebase auth連携用のidToken取得
         */
        const idToken = await getAccessToken({ forceUpdateToken: true })
        if (!idToken) throw new Error('get jwtToke failed')

        /**
         * web3auth <=> firebase auth連携
         */
        await import('@/core/web3/web3auth').then(m =>
          m.connectToWeb3Auth(idToken)
        )

        return
      },
      openTorusWallet: async () => {
        const idToken = await getAccessToken({ forceUpdateToken: true })
        if (!idToken) throw new Error('get jwtToke failed')

        await import('@/core/web3/web3auth').then(m =>
          m.openTorusWallet(idToken)
        )
      },
      onSubmitPasswordSetting: async ({ password }) => {
        if (!user?.email) throw new Error(t('unregisteredEmail'))

        /**
         * @description
         * iOS16ではブラウザでOAuth連携できないため、エラーを表示する
         * https://github.com/firebase/firebase-js-sdk/issues/6716
         */
        if (
          deviceInfo?.operatingSystem === 'ios' &&
          Number(deviceInfo.webViewVersion) >= 16.1
        ) {
          throw new Error(t('iOSOAuthMessage', { ns: 'integrations' }))
        }

        return linkEmail(user.email, password)
      },
    },
  }
})

const QUERY = gql`
  query Wallet($gachaTicketId: ID!) {
    user {
      id
      displayName
      gachaTicket(id: $gachaTicketId) {
        available
        formattedAvailable @client
      }
      cryptoWallet {
        address
        walletType
      }
    }
    prepaidPointBalance {
      balance
      formattedPrepaidPointBarance @client
    }
    gachaTicket(id: $gachaTicketId) {
      ...GachaTicketConnection
    }
  }
  ${GachaTicketConnectionFragmentDoc}
`
